/**
 * Status vocabulary approved by Andres Eulacio, 2026-09-22 (PROGRESS.md, Phase 2 PLAN).
 * State and progress words only; no pass/fail verdicts. Tone: 'neutral' everywhere except a committed
 * action plan that is materially off track, the only thing that may render red.
 */
/**
 * The verdict words the portal never uses about a firm (CLAUDE.md: informative, never judgmental). The one list:
 * the status labels, the copy-lint test over every builder and PDF string, and the browser tests all read it.
 * `extra` adds words banned in one place only (a status label may not say "good", "critical" or "red", but copy may
 * say "good practice" and "Critical protections").
 */
export const VERDICT_WORDS = ['pass', 'passed', 'fail', 'failed', 'failing', 'non-compliant', 'noncompliant', 'compliant', 'bad', 'poor', 'weak', 'material weakness', 'significant deficiency'];
export const verdictPattern = (extra = []) => new RegExp(`\\b(${[...VERDICT_WORDS, ...extra].join('|')})\\b`, 'i');
/**
 * A control's result, wherever it comes from (CDW, the firm's own rating, National's review), on every page (DEC-23):
 * "Fully in place"; "In place" alone means fully or partly. The one list of these names: the other names below are
 * this list, so they cannot drift apart.
 */
export const RESULT_STATUS = {
    no_gap: { label: 'Fully in place', tone: 'in_place' },
    partial_gap: { label: 'Partly in place', tone: 'progress' },
    gap: { label: 'Not yet in place', tone: 'neutral' },
    out_of_scope: { label: 'Out of scope', tone: 'neutral' },
};
export const CDW_GAP_STATUS = RESULT_STATUS;
export const SURVEY_STATUS = {
    achieved: { label: 'Reported in place', tone: 'in_place' },
    not_achieved: { label: 'Not reported in place', tone: 'neutral' },
    no_response: { label: 'No response', tone: 'neutral' },
};
export const TRACKING_STATUS = {
    not_yet_planned: { label: 'Not yet planned', tone: 'neutral' },
    planned: { label: 'Planned', tone: 'neutral' },
    in_progress: { label: 'In progress', tone: 'progress' },
    in_place: { label: 'In place', tone: 'in_place' },
    risk_accepted: { label: 'Risk accepted', tone: 'neutral' },
    not_applicable: { label: 'Not applicable', tone: 'neutral' },
};
export const DELIVERY_STATUS = {
    not_yet_committed: { label: 'Owner and date not yet confirmed', tone: 'neutral' },
    on_track: { label: 'On track', tone: 'progress' },
    at_risk: { label: 'At risk', tone: 'attention' },
    off_track: { label: 'Off track', tone: 'off_track' },
    complete: { label: 'Complete', tone: 'in_place' },
};
/**
 * A finding's state on Leadership (5C, FindingStatus): its plans' delivery status, or the risk accepted by the
 * firm's leaders. The off-track red is still only a committed plan off track.
 */
export const FINDING_STATUS = {
    ...DELIVERY_STATUS,
    // DEC-49/53: owners and dates stay on the firms' own pages; a finding with no committed plan has not started (PL-04).
    not_yet_committed: { label: 'Not started yet', tone: 'neutral' },
    accepted: { label: 'Risk accepted', tone: 'reviewed' },
};
export const RISK_SEVERITY = {
    very_high: { label: 'Very high', tone: 'attention' },
    high: { label: 'High', tone: 'attention' },
    medium: { label: 'Medium', tone: 'neutral' },
    low: { label: 'Low', tone: 'neutral' },
    very_low: { label: 'Very low', tone: 'neutral' },
};
export const ACCEPTANCE_STATUS = {
    pending_approval: { label: 'Awaiting approval', tone: 'attention' },
    active: { label: 'Accepted', tone: 'neutral' },
    superseded: { label: 'Replaced by a later decision', tone: 'neutral' },
    expired: { label: 'Review date reached', tone: 'attention' },
    withdrawn: { label: 'Withdrawn', tone: 'neutral' },
};
/** Provenance labels: every measure says what it represents. */
export const PROVENANCE = {
    cdw_validated: 'Assessed by CDW, 2026',
    self_assessed: 'Firm self-assessment, March 2025',
    firm_reported: 'Reported by the firm',
    national_reviewed: 'Reviewed by National IT',
    self_assessed_2026: 'Self-assessed by the firm, 2026',
};
/**
 * The label on a baseline worked out from the March 2025 survey, shown only for a firm with no current
 * results (no CDW, no 2026 self-assessment): the firm's own answers, never an independent result.
 */
export const SURVEY_BASELINE_MEASURE = "As of March 2025: the firm's own survey answers";
/** A firm's own 2026 rating while it waits for CDW (migration 0011). */
export const SELF_STATUS = RESULT_STATUS;
/** A result's name alone, from RESULT_STATUS. */
export const RESULT_LABEL = Object.fromEntries(Object.entries(RESULT_STATUS).map(([k, l]) => [k, l.label]));
export const resultLabel = (k) => RESULT_LABEL[k] ?? k;
/** A firm's submitted assessment, as it moves through National review. */
export const SUBMISSION_STATUS = {
    submitted: { label: 'Awaiting review', tone: 'neutral' },
    returned: { label: 'Returned with questions', tone: 'attention' },
    accepted: { label: 'Reviewed by National IT', tone: 'reviewed' },
    superseded: { label: 'Replaced by a newer submission', tone: 'neutral' },
};
/** A question a firm sent to National IT through the National agent (migration 0017). */
export const NATIONAL_REQUEST_STATUS = {
    open: { label: 'Open', tone: 'progress' },
    answered: { label: 'Answered', tone: 'in_place' },
    closed: { label: 'Closed', tone: 'neutral' },
};
export const TRACKING_STATUSES = Object.keys(TRACKING_STATUS);
export function labelOf(map, key) {
    return (key && map[key]) || { label: 'Not recorded', tone: 'neutral' };
}
export * from './content-status.js';
export * from './plain-language.js';
export * from './risk-impact.js';
export * from './implementation.js';
export * from './leadership.js';
export * from './leadership-lines.js';
export * from './leadership-cards.js';
export * from './national-help.js';
export * from './national-services.js';
export * from './bti-evaluation.js';
// The one counting rule (in place = fully or partly), for the web as well as the API.
export * from './scoring.js';
export * from './worklist.js';
// What waits for National IT, said one way on the National view (NEXT PASS 5).
export * from './national-work.js';
//# sourceMappingURL=vocabulary.js.map